/* eslint-disable no-param-reassign */
import axios from 'axios';

import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { updateAuthTokens } from '../redux/auth/actions';

import { API_URL } from '../attrs/paths';
import { getReplacementTokensForSameUser } from './refreshTokenSession';
import { isProductionMonitoringAccessDenied } from '../attrs/accessErrors';
import { getRequestSession, LOGIN_SESSION_STORAGE_KEY, sameLoginSession } from './loginSession';
import { endSessionWithoutAccess } from './accessDeniedSession';

function getAccessToken() {
  return localStorage.getItem('accessToken');
}

function getRefreshToken() {
  return localStorage.getItem('refreshToken');
}

const paramsSerializer = {
  serialize: params => {
    const sp = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value == null) return;
      if (Array.isArray(value)) {
        value.forEach(v => sp.append(key, v));
      } else {
        sp.append(key, value);
      }
    });
    return sp.toString();
  }
};

const instance = axios.create({
  baseURL: API_URL,
  paramsSerializer
});

const refreshInstance = axios.create({
  baseURL: API_URL,
  paramsSerializer
});

export const refreshAuthTokens = () => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports,global-require,@typescript-eslint/no-var-requires
  const getStore = require('../redux/store').default;
  const { store } = getStore();
  const refreshToken = getRefreshToken();
  const requestSession = getRequestSession(refreshToken);

  return refreshInstance
    .get('/v1/tokens', {
      headers: {
        Authorization: `Bearer ${refreshToken}`
      }
    })
    .then(tokenRefreshResponse => {
      if (!getRefreshToken()) {
        throw new Error('Session ended while refreshing tokens');
      }
      if (!sameLoginSession(requestSession, getRequestSession(getRefreshToken()))) {
        throw new Error('Session changed while refreshing tokens');
      }
      localStorage.setItem('accessToken', tokenRefreshResponse.data.access);
      localStorage.setItem('refreshToken', tokenRefreshResponse.data.refresh);
      store.dispatch(updateAuthTokens(tokenRefreshResponse.data));

      return tokenRefreshResponse.data;
    })
    .catch(error => {
      if (isProductionMonitoringAccessDenied(error)) {
        endSessionWithoutAccess(error, requestSession, store);
        throw error;
      }
      if (error.response?.status === 503 || !sameLoginSession(requestSession, getRequestSession(getRefreshToken()))) {
        throw error;
      }
      const currentAccessToken = getAccessToken();
      const currentRefreshToken = getRefreshToken();
      const replacementTokens = getReplacementTokensForSameUser(refreshToken, currentAccessToken, currentRefreshToken);

      if (replacementTokens) {
        store.dispatch(updateAuthTokens(replacementTokens));
        return replacementTokens;
      }

      // Another tab may already have rotated this single-use refresh token.
      // Only invalidate the session when the failed token is still current.
      if (currentRefreshToken === refreshToken) {
        localStorage.removeItem(LOGIN_SESSION_STORAGE_KEY);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        store.dispatch(updateAuthTokens(null));
      }

      throw error;
    });
};

// Function that will be called to refresh authorization
const refreshAuthLogic = failedRequest =>
  refreshAuthTokens().then(tokens => {
    if (tokens) {
      failedRequest.response.config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  });

instance.interceptors.request.use(request => {
  const accessToken = getAccessToken();
  request.headers.Authorization = `Bearer ${accessToken}`;
  request.notifyAccessSession = getRequestSession(accessToken);

  return request;
});

instance.interceptors.response.use(undefined, error => {
  if (isProductionMonitoringAccessDenied(error)) {
    // Using require here to avoid circular dependency issues with the store.
    // eslint-disable-next-line @typescript-eslint/no-require-imports,global-require,@typescript-eslint/no-var-requires
    const { store } = require('../redux/store').default();
    endSessionWithoutAccess(error, error.config?.notifyAccessSession, store);
  }
  return Promise.reject(error);
});

createAuthRefreshInterceptor(instance, refreshAuthLogic, {
  statusCodes: [401, 403],
  shouldRefresh: error => !isProductionMonitoringAccessDenied(error) && [401, 403].includes(error.response?.status)
});

export default instance;
